/* eslint-disable max-lines */
import React from 'react'
import { CrossRefPaper, paperInitState } from '@cpsy-rmap-project/web-core/crossref'
import { Paper } from '@cpsy-rmap-project/web-core/repositories'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useLocation, useHistory } from 'react-router-dom'
import { useUsecasePaper } from '../../../usecases/paper'
import { removedUnspecifiedTag } from '../../../utils/paper'
import { useLoading } from '../../atoms/Loading'
import { useLogin } from '../../organisms/Login'
// import { usePaper } from '../../organisms/Paper'
import { varidatePaper } from '../SubmitModelDialog/varidation'

// eslint-disable-next-line no-shadow
export enum EditModelSteps {
  'TAG_MODEL' = 0,
  'TAG_DISEASE' = 1,
  'TAG_RDOC' = 2,
  'INPUT_DETAILS' = 3,
  'FINISHED' = 4
}

interface DialogState {
  open: boolean
  isOwner: boolean
  activeStep: number
  // フラグ用
  isReadySubmit: boolean
}

interface Handle {
  handleResetPaper: () => void
  handleResetState: () => void
  handleOpen: (paper: Paper, isOwner: boolean, reset?: boolean) => void
  handleClose: () => void
  handleBack: () => void
  handleNext: () => Promise<void>
  handleSubmit: () => Promise<void>
  handleDetailsChange: (update: Partial<CrossRefPaper>) => void
  handleIsReadySubmit: (_isReadySubmit: boolean) => void
}

const dialogInitState: DialogState = {
  open: false,
  isOwner: false,
  activeStep: 0,
  // フラグ用
  isReadySubmit: false
}

const handleInitState: Handle = {
  handleResetPaper: () => {},
  handleResetState: () => {},
  handleOpen: () => {},
  handleClose: () => {},
  handleBack: () => {},
  handleNext: async () => {},
  handleSubmit: async () => {},
  handleDetailsChange: (_update: Partial<CrossRefPaper>) => {},
  handleIsReadySubmit: (_isReadySubmit: boolean) => {}
}

const EditModelDialogContext = React.createContext<
  { dialog: DialogState } & { paper: CrossRefPaper } & { handle: Handle }
>({
  dialog: dialogInitState,
  paper: paperInitState,
  handle: handleInitState
})

// eslint-disable-next-line max-lines-per-function
export const EditModelDialogProvider: React.FC = ({ children }) => {
  // Paper State
  // const { handler } = usePaper()

  // Dialog State
  const [open, setOpen] = React.useState(dialogInitState.open)
  const [activeStep, setActiveStep] = React.useState(dialogInitState.activeStep)
  const [isOwner, setIsOwner] = React.useState<boolean>(false)
  const [uid, setUid] = React.useState<string>('')

  // Paper State
  const [paperId, setPaperId] = React.useState<string>('')
  const [paper, setPaper] = React.useState<CrossRefPaper>(paperInitState)
  const [originalPaper, setOriginalPaper] = React.useState<CrossRefPaper>(paperInitState)
  const [isReadySubmit, setIsReadySubmit] = React.useState<boolean>(dialogInitState.isReadySubmit)

  // Paper読み込みフラグ
  const [initializedLoadPaper, setInitializedLoadPaper] = React.useState(false)

  /**
   * Router系設定
   */
  const history = useHistory()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const QUERY_STRING_KEY = 'mode'
  const QUERY_STRING_VALUE = 'edit'
  const queryStringsAddFlag = query.get(QUERY_STRING_KEY)

  // URLクエリを追加する(?QUERY_STRING_KEYを付与する)
  const addUrlParams = React.useCallback(() => {
    const nextquery = new URLSearchParams(location.search)
    nextquery.set(QUERY_STRING_KEY, QUERY_STRING_VALUE)
    history.push(`${location.pathname}?${nextquery.toString()}`)
  }, [history, location])

  // URLクエリを除外する(?QUERY_STRING_KEYを消す)
  const removeUrlParams = React.useCallback(() => {
    const nextquery = new URLSearchParams(location.search)
    nextquery.delete(QUERY_STRING_KEY)
    history.push(`${location.pathname}?${nextquery.toString()}`)
    setPaperId('') // データ入力判定用に使う
  }, [history, location])

  // URLから移動してきた場合モーダルをオープンする
  React.useEffect(() => {
    if (queryStringsAddFlag === QUERY_STRING_VALUE) {
      if (paperId !== '') {
        setOpen(true)
      } else {
        removeUrlParams()
      }
    } else {
      setOpen(false)
    }
  }, [paperId, queryStringsAddFlag, removeUrlParams])

  //

  // Firebase
  const login = useLogin()
  const firestorePaper = useUsecasePaper()

  // 通知用
  const { handleIsLoading } = useLoading()
  const snackbar = useSnackbar()

  const handleResetPaper = React.useCallback(() => {
    setPaper(paperInitState)
    setOriginalPaper(paperInitState)
    setUid('')
    setIsOwner(false)
    setInitializedLoadPaper(false)
  }, [])

  const handleResetState = React.useCallback(() => {
    setActiveStep(EditModelSteps.TAG_MODEL)
    handleResetPaper()
    setIsReadySubmit(false)
  }, [handleResetPaper])

  const handleOpen = React.useCallback(
    (_paper: Paper, _isOwner: boolean, _reset?: boolean) => {
      if (_reset === true || !initializedLoadPaper) {
        // 初期化
        setActiveStep(EditModelSteps.TAG_MODEL)
        setIsReadySubmit(false)
        console.log('編集Paperの読み込み')
        const removedUnspecifiedTagPaper = removedUnspecifiedTag(_paper)
        setPaper(removedUnspecifiedTagPaper)
        setOriginalPaper(removedUnspecifiedTagPaper)
        setPaperId(removedUnspecifiedTagPaper.id)
        setUid(removedUnspecifiedTagPaper.uid)
        setInitializedLoadPaper(true)
      }
      setIsOwner(_isOwner)
      addUrlParams()
    },
    [addUrlParams, initializedLoadPaper]
  )

  const handleClose = React.useCallback(() => {
    removeUrlParams()
  }, [removeUrlParams])

  const handleIsReadySubmit = React.useCallback((_isReadySubmit: boolean) => {
    setIsReadySubmit(_isReadySubmit)
  }, [])

  const handleBack = React.useCallback(() => {
    switch (activeStep) {
      case EditModelSteps.TAG_MODEL:
        handleClose()
        handleResetState()
        break
      case EditModelSteps.TAG_DISEASE:
        setActiveStep(EditModelSteps.TAG_MODEL)
        break
      case EditModelSteps.TAG_RDOC:
        setActiveStep(EditModelSteps.TAG_DISEASE)
        break
      case EditModelSteps.INPUT_DETAILS:
        setActiveStep(EditModelSteps.TAG_RDOC)
        break
      case EditModelSteps.FINISHED:
        setActiveStep(EditModelSteps.INPUT_DETAILS)
        break
      default:
        // 想定外
        break
    }
  }, [activeStep, handleClose, handleResetState])

  const handleEdit = React.useCallback(async () => {
    if (isReadySubmit) {
      const varidateErrors = varidatePaper(paper)
      if (!_.isEmpty(varidateErrors)) {
        varidateErrors.forEach(error => {
          snackbar.enqueueSnackbar(error, { variant: 'error' })
        })
        handleIsReadySubmit(false)
        handleBack()
        handleIsLoading(false)
      } else if (!login.isLoggedIn()) {
        login.handleOpen()
        snackbar.enqueueSnackbar('Require Login.', { variant: 'error' })
        handleIsReadySubmit(false)
        handleBack()
        handleIsLoading(false)
      } else {
        try {
          // edit処理（DB登録＋内部Papers更新）
          /* const editedPaper = */ await firestorePaper.editPaper(
            paperId,
            {
              ...paper,
              draftByAutoTag: false
            },
            uid
          )
          // handler.edit(editedPaper)
          //
          snackbar.enqueueSnackbar('Successfully edited.', {
            variant: 'success'
          })
          setIsReadySubmit(false)
          handleClose()
          handleResetState()
        } catch (e) {
          console.error(e)
          snackbar.enqueueSnackbar('Oops! something went wrong.', {
            variant: 'error'
          })
          handleIsReadySubmit(false)
          handleBack()
        } finally {
          handleIsLoading(false)
        }
      }
    }
    // snackbarとfirestorePaperとloginを無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleBack,
    handleClose,
    handleIsLoading,
    handleIsReadySubmit,
    handleResetState,
    isReadySubmit,
    paper,
    paperId,
    uid
  ])

  const handleEditRequest = React.useCallback(async () => {
    if (isReadySubmit) {
      const varidateErrors = varidatePaper(paper)
      if (!_.isEmpty(varidateErrors)) {
        varidateErrors.forEach(error => {
          snackbar.enqueueSnackbar(error, { variant: 'error' })
        })
        handleIsReadySubmit(false)
        handleBack()
        handleIsLoading(false)
      } else if (!login.isLoggedIn() || !login.fbUser) {
        login.handleOpen()
        snackbar.enqueueSnackbar('Require Login.', { variant: 'error' })
        handleIsReadySubmit(false)
        handleBack()
        handleIsLoading(false)
      } else {
        try {
          await firestorePaper.editRequestPaper(paperId, originalPaper, paper, login.fbUser)
          snackbar.enqueueSnackbar('Edit request has been submitted.', {
            variant: 'success'
          })
          setIsReadySubmit(false)
          handleClose()
          handleResetState()
        } catch (e: any) {
          console.error(e)
          if (e.message === 'Nothing changes.') {
            snackbar.enqueueSnackbar('Error: Nothing changes.', {
              variant: 'error'
            })
          } else {
            snackbar.enqueueSnackbar('Oops! something went wrong.', {
              variant: 'error'
            })
          }
          handleIsReadySubmit(false)
          handleBack()
        } finally {
          handleIsLoading(false)
        }
      }
    }
    // snackbarとfirestorePaperとloginを無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleBack,
    handleClose,
    handleIsLoading,
    handleIsReadySubmit,
    handleResetState,
    isReadySubmit,
    originalPaper,
    paper
  ])

  const handleNext = React.useCallback(async () => {
    switch (activeStep) {
      case EditModelSteps.TAG_MODEL: {
        setActiveStep(EditModelSteps.TAG_DISEASE)
        break
      }
      case EditModelSteps.TAG_DISEASE: {
        setActiveStep(EditModelSteps.TAG_RDOC)
        break
      }
      case EditModelSteps.TAG_RDOC: {
        setActiveStep(EditModelSteps.INPUT_DETAILS)
        break
      }
      case EditModelSteps.INPUT_DETAILS: {
        handleIsLoading(true)
        setActiveStep(EditModelSteps.FINISHED)
        break
      }
      default: {
        // 想定外
        break
      }
    }
  }, [activeStep, handleIsLoading])

  const handleSubmit = React.useCallback(async () => {
    if (isOwner) {
      await handleEdit()
    } else {
      await handleEditRequest()
    }
  }, [handleEdit, handleEditRequest, isOwner])

  const handleDetailsChange = React.useCallback(
    (update: Partial<CrossRefPaper>) => {
      setPaper({ ...paper, ...update })
    },
    [paper]
  )

  return (
    <EditModelDialogContext.Provider
      value={{
        dialog: { open, isOwner, activeStep, isReadySubmit },
        paper,
        handle: {
          handleResetPaper,
          handleResetState,
          handleOpen,
          handleClose,
          handleBack,
          handleNext,
          handleSubmit,
          handleDetailsChange,
          handleIsReadySubmit
        }
      }}
    >
      {children}
    </EditModelDialogContext.Provider>
  )
}

export const useEditModelDialog = () => {
  const { dialog, paper, handle } = React.useContext(EditModelDialogContext)
  return { dialog, paper, handle }
}
